import React, { useState, useEffect } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';

import toast from 'react-hot-toast';
import apiClient from '../config/axios';
import { useWorkspace } from '../contexts/WorkspaceContext';
import ModalPortal from '../components/ModalPortal';

import { ArrowLeft, Save, Loader2, FileText, Code, Sparkles, Eye, X } from 'lucide-react';

const TemplateCreator = () => {
    const navigate = useNavigate();

    const location = useLocation();
    const { currentWorkspace } = useWorkspace();
    const queryClient = useQueryClient();

    const navigationState = location.state || {};
    const [templateType, setTemplateType] = useState(navigationState.templateType || 'text');
    const [formData, setFormData] = useState({
        name: navigationState.templateData?.name || '',
        subject: navigationState.templateData?.subject || '',
        content: navigationState.templateData?.content || '',
        prompt: navigationState.templateData?.prompt || '',
        template_type: navigationState.templateData?.template_type || 'text'
    });

    const editingTemplateId = navigationState.editingTemplateId || null;
    const [showPreview, setShowPreview] = useState(false);

    const [aiWritingStatus, setAiWritingStatus] = useState('Generating');
    const [aiOutputType, setAiOutputType] = useState('text'); // 'text' or 'html' for AI generation
    const [generatedContent, setGeneratedContent] = useState(null);
    const [showAiModifyPrompt, setShowAiModifyPrompt] = useState(false); // For edit mode AI modification
    const [aiModifyPrompt, setAiModifyPrompt] = useState(''); // User's modification prompt
    const [showEditPreview, setShowEditPreview] = useState(false); // Preview toggle for edit mode
    const [showSubjectAiModal, setShowSubjectAiModal] = useState(false); // Subject AI modal
    const [subjectPrompt, setSubjectPrompt] = useState(''); // Subject generation prompt
    const [generatedSubjects, setGeneratedSubjects] = useState([]); // AI generated subject options
    const [selectedSubject, setSelectedSubject] = useState(''); // Selected subject by user
    const [subjectGenerating, setSubjectGenerating] = useState(false); // Loading state for subject generation

    // Mutation for generating AI preview (without saving)
    const generatePreviewMutation = useMutation(
        async () => {
            const headers = { 'x-workspace-id': currentWorkspace.id };
            const response = await apiClient.post('/api/email-templates/create-ai', {
                name: formData.name,
                prompt: formData.prompt,
                template_type: aiOutputType
            }, { headers });
            return response.data;
        },
        {
            onSuccess: (data) => {
                if (data.template) {
                    setGeneratedContent(data.template);
                    setShowPreview(true);
                }
            },
            onError: (error) => {
                toast.error(error.response?.data?.error || 'Failed to generate preview');
            }
        }
    );

    // Mutation for AI modification in edit mode
    const modifyWithAIMutation = useMutation(
        async (modificationPrompt) => {
            const headers = { 'x-workspace-id': currentWorkspace.id };
            // Detect if current content is HTML
            const contentType = isHTMLContent(formData.content) ? 'html' : 'text';

            // Create a combined prompt that includes the current content and the modification request
            // For HTML content, add strict instruction to return ONLY HTML code
            let combinedPrompt = '';
            if (contentType === 'html') {
                combinedPrompt = `Here is the current HTML email content:\n${formData.content}\n\nModify it according to: ${modificationPrompt}\n\nIMPORTANT: Return ONLY the complete HTML code. No explanations, no markdown formatting, no comments, no additional text. Start with <!DOCTYPE html> and end with </html>. Output ONLY the HTML code.`;
            } else {
                combinedPrompt = `Current email content:\n${formData.content}\n\nPlease modify it according to: ${modificationPrompt}`;
            }

            const response = await apiClient.post('/api/email-templates/create-ai', {
                name: formData.name,
                prompt: combinedPrompt,
                template_type: contentType
            }, { headers });
            return response.data;
        },
        {
            onSuccess: (data) => {
                if (data.template) {
                    setFormData({ ...formData, content: data.template.content });
                    setShowAiModifyPrompt(false);
                    setAiModifyPrompt('');
                    toast.success('Content modified successfully!');
                }
            },
            onError: (error) => {
                toast.error(error.response?.data?.error || 'Failed to modify content');
            }
        }
    );

    // Generate AI subject options
    const generateSubjectOptions = async () => {
        if (!subjectPrompt.trim()) {
            toast.error('Please enter a prompt for subject generation');
            return;
        }

        try {
            setSubjectGenerating(true);
            const headers = { 'x-workspace-id': currentWorkspace.id };

            const response = await apiClient.post('/api/email-templates/generate-subjects', {
                prompt: subjectPrompt,
                current_subject: formData.subject,
                content: formData.content
            }, { headers });

            if (response.data.subjects && response.data.subjects.length > 0) {
                setGeneratedSubjects(response.data.subjects);
            } else {
                toast.error('No subjects generated');
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to generate subjects');
        } finally {
            setSubjectGenerating(false);
        }
    };

    // Apply selected subject
    const applySelectedSubject = () => {
        if (!selectedSubject) {
            toast.error('Please select a subject first');
            return;
        }

        setFormData({ ...formData, subject: selectedSubject });
        setShowSubjectAiModal(false);
        setSubjectPrompt('');
        setGeneratedSubjects([]);
        setSelectedSubject('');
        toast.success('Subject updated successfully!');
    };

    const createTemplateMutation = useMutation(
        async (templateData) => {
            const headers = { 'x-workspace-id': currentWorkspace.id };


            if (editingTemplateId) {
                const response = await apiClient.put(`/api/email-templates/${editingTemplateId}`, {
                    name: templateData.name,
                    subject: templateData.subject,
                    content: templateData.content,
                    is_active: true
                }, { headers });
                return response.data;
            }

            let endpoint = '';
            let payload = {};

            if (templateType === 'ai') {
                endpoint = '/api/email-templates/create-ai';
                payload = {
                    name: templateData.name,
                    prompt: templateData.prompt,

                    template_type: aiOutputType // Use aiOutputType instead of templateData.template_type
                };
            } else {
                endpoint = templateType === 'html' ? '/api/email-templates/create-html' : '/api/email-templates/create-text';
                payload = {
                    name: templateData.name,
                    subject: templateData.subject,

                    content: templateData.content
                };
            }

            const response = await apiClient.post(endpoint, payload, { headers });
            return response.data;
        },
        {
            onSuccess: (data) => {

                toast.success(editingTemplateId ? 'Template updated!' : 'Template created!');
                queryClient.invalidateQueries(['emailTemplates', currentWorkspace?.id]);
                navigate('/email-templates');
            },
            onError: (error) => {

                toast.error(error.response?.data?.error || 'Failed');
            }
        }
    );

    // Animate AI writing status
    useEffect(() => {
        if (createTemplateMutation.isLoading) {
            const messages = ['Generating', 'Writing code', 'Adding styles', 'Finalizing', 'Generating'];
            let currentIndex = 0;
            const interval = setInterval(() => {
                currentIndex = (currentIndex + 1) % messages.length;
                setAiWritingStatus(messages[currentIndex]);
            }, 1500);
            return () => clearInterval(interval);
        }
    }, [createTemplateMutation.isLoading]);

    // Helper function to check if content is HTML
    const isHTMLContent = (content) => {
        if (!content) return false;
        // Check for common HTML tags or patterns
        const htmlPattern = /<[a-z][\s\S]*>/i;
        return htmlPattern.test(content);
    };

    const handleSubmit = () => {
        createTemplateMutation.mutate(formData);
    };

    return (

        <>
            <style>{`
                @keyframes gradient {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
            `}</style>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">

                {/* Enhanced Header */}
                <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 shadow-lg">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                        <div className="flex items-center justify-between h-20">
                            <button onClick={() => navigate('/email-templates')} className="flex items-center space-x-2 text-white hover:text-blue-100 transition-colors group">
                                <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
                                <span className="font-medium">Back</span>
                            </button>

                            <div className="text-center">
                                <h1 className="text-2xl font-bold text-white">{editingTemplateId ? 'Edit Email Template' : 'Create Email Template'}</h1>
                                <p className="text-sm text-blue-100 mt-1">Design your perfect email</p>
                            </div>
                            <div className="w-24"></div>
                        </div>
                    </div>
                </div>


                <div className="max-w-7xl mx-auto px-4 py-8">
                    {!editingTemplateId ? (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left Sidebar */}
                            <div className="lg:col-span-1">

                                <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4">
                                    {/* Choose Template Type */}
                                    <div className="mb-8">

                                        <div className="flex items-center mb-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
                                                <h2 className="text-lg font-bold text-gray-900">Template Type</h2>
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <button
                                                onClick={() => setTemplateType('text')}

                                                className={`w-full p-4 rounded-xl border-2 transition-all transform hover:scale-102 ${templateType === 'text' ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-md' : 'border-gray-200 hover:border-blue-300 bg-white'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${templateType === 'text' ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gray-100'}`}>
                                                        <FileText className={`w-5 h-5 ${templateType === 'text' ? 'text-white' : 'text-gray-600'}`} />
                                                    </div>
                                                    <div className="text-left">

                                                        <p className="font-bold text-gray-900 text-sm">Text</p>
                                                        <p className="text-xs text-gray-500">Plain text email</p>
                                                    </div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => setTemplateType('html')}

                                                className={`w-full p-4 rounded-xl border-2 transition-all transform hover:scale-102 ${templateType === 'html' ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-md' : 'border-gray-200 hover:border-green-300 bg-white'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${templateType === 'html' ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gray-100'}`}>
                                                        <Code className={`w-5 h-5 ${templateType === 'html' ? 'text-white' : 'text-gray-600'}`} />
                                                    </div>
                                                    <div className="text-left">

                                                        <p className="font-bold text-gray-900 text-sm">HTML</p>
                                                        <p className="text-xs text-gray-500">Rich HTML design</p>
                                                    </div>
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => setTemplateType('ai')}

                                                className={`w-full p-4 rounded-xl border-2 transition-all transform hover:scale-102 ${templateType === 'ai' ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-md' : 'border-gray-200 hover:border-purple-300 bg-white'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${templateType === 'ai' ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gray-100'}`}>
                                                        <Sparkles className={`w-5 h-5 ${templateType === 'ai' ? 'text-white' : 'text-gray-600'}`} />
                                                    </div>
                                                    <div className="text-left">

                                                        <p className="font-bold text-gray-900 text-sm">AI Generated</p>
                                                        <p className="text-xs text-gray-500">AI-powered content</p>
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Quick Templates */}

                                    {templateType === 'ai' && (
                                        <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 rounded-xl p-5 border border-purple-100 shadow-md">
                                            <div className="flex items-center mb-4">
                                                <Sparkles className="w-5 h-5 text-purple-600 mr-2" />
                                                <h3 className="text-lg font-bold text-gray-900">Quick Templates</h3>
                                            </div>
                                            <div className="grid grid-cols-1 gap-2">
                                                {['Welcome Email', 'Promotional Email', 'Newsletter', 'Thank You Email', 'Product Launch', 'Event Invitation'].map((name) => (
                                                    <button

                                                        key={name}
                                                        onClick={() => setFormData({ ...formData, name, prompt: `Create a professional ${name.toLowerCase()}` })}
                                                        className="p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-400 hover:bg-purple-50 transition-all text-sm font-medium text-gray-700 hover:text-purple-700 transform hover:scale-102 shadow-sm text-left"
                                                    >
                                                        {name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                    )}
                                </div>
                            </div>


                            {/* Right Content Box */}
                            <div className="lg:col-span-2">
                                <div className="bg-white rounded-2xl shadow-lg p-8">
                                    <div className="space-y-6">
                                        <div>

                                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                <span className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                                                Template Name
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}

                                                className="w-full px-5 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white font-medium"
                                                placeholder="Enter a name for your template"
                                            />
                                        </div>

                                        {templateType === 'ai' ? (

                                            <>
                                                {/* Output Type Selection */}
                                                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-5 border-2 border-purple-100 shadow-md">
                                                    <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                        <Sparkles className="w-4 h-4 text-purple-600" />
                                                        AI Output Format
                                                    </label>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <button

                                                            onClick={() => setAiOutputType('text')}
                                                            className={`p-4 rounded-xl border-2 transition-all transform hover:scale-102 ${aiOutputType === 'text'
                                                                ? 'border-blue-500 bg-blue-50 shadow-md'
                                                                : 'border-gray-200 hover:border-blue-300 bg-white'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <FileText className={`w-5 h-5 ${aiOutputType === 'text' ? 'text-blue-600' : 'text-gray-600'}`} />
                                                                <span className={`font-semibold ${aiOutputType === 'text' ? 'text-blue-700' : 'text-gray-700'}`}>
                                                                    Text
                                                                </span>
                                                            </div>
                                                        </button>
                                                        <button

                                                            onClick={() => setAiOutputType('html')}
                                                            className={`p-4 rounded-xl border-2 transition-all transform hover:scale-102 ${aiOutputType === 'html'
                                                                ? 'border-green-500 bg-green-50 shadow-md'
                                                                : 'border-gray-200 hover:border-green-300 bg-white'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <Code className={`w-5 h-5 ${aiOutputType === 'html' ? 'text-green-600' : 'text-gray-600'}`} />
                                                                <span className={`font-semibold ${aiOutputType === 'html' ? 'text-green-700' : 'text-gray-700'}`}>
                                                                    HTML
                                                                </span>
                                                            </div>
                                                        </button>
                                                    </div>
                                                </div>


                                                {/* AI Prompt */}
                                                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border-2 border-purple-100 shadow-md">
                                                    <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                                        <Sparkles className="w-4 h-4 text-purple-600" />
                                                        AI Prompt
                                                    </label>
                                                    <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                                                        <p className="text-xs text-blue-700 font-medium flex items-center gap-1">
                                                            ✨ AI will generate both email subject and content together
                                                        </p>
                                                    </div>
                                                    <textarea
                                                        value={formData.prompt}
                                                        onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                                                        className="w-full px-5 py-4 border-2 border-purple-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-medium"
                                                        rows={6}
                                                        placeholder="Describe your email template in detail... (e.g., 'Create a welcome email for new users with company branding')"
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div>

                                                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                        <span className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                                                        Email Subject
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.subject}
                                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}

                                                        className="w-full px-5 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white font-medium"
                                                        placeholder="Enter email subject line"
                                                    />
                                                    {/* Personalization helper */}
                                                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                                                        <div className="font-semibold mb-1">Personalization placeholders</div>
                                                        <div className="space-y-1">
                                                            <p>Use {`{name}`} or {`{{name}}`} to insert the contact name.</p>
                                                            <p>Use {`{first_name}`} or {`{{first_name}}`} for first name only.</p>
                                                            <p>Use {`{email}`} or {`{{email}}`} to insert the contact email.</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>

                                                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                        <span className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                                                        Email Content
                                                    </label>
                                                    <textarea
                                                        value={formData.content}
                                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}

                                                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white font-medium resize-none"
                                                        rows={14}
                                                        placeholder="Write your email content here..."
                                                    />
                                                    {/* Live personalization preview */}
                                                    <TemplatePersonalizationPreview subject={formData.subject} content={formData.content} />
                                                </div>
                                            </>
                                        )}
                                    </div>


                                    <div className="flex justify-between items-center mt-10 pt-8 border-t-2 border-gray-100">
                                        <button
                                            onClick={() => navigate('/email-templates')}

                                            className="px-8 py-3.5 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold text-gray-700 hover:text-gray-900 transform hover:scale-105"
                                        >
                                            Cancel
                                        </button>

                                        <div className="flex gap-3">
                                            {templateType === 'ai' && (
                                                <button

                                                    onClick={() => generatePreviewMutation.mutate()}
                                                    disabled={generatePreviewMutation.isLoading || !formData.name || !formData.prompt}
                                                    className="px-6 py-3.5 border-2 border-purple-500 text-purple-600 rounded-xl hover:bg-purple-50 transition-all font-semibold flex items-center gap-2 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Preview the generated template without saving"
                                                >
                                                    {generatePreviewMutation.isLoading ? (
                                                        <Loader2 className="h-5 w-5 animate-spin" />
                                                    ) : (
                                                        <Eye className="h-5 w-5" />

                                                    )}
                                                    Preview AI
                                                </button>
                                            )}
                                            <button
                                                onClick={handleSubmit}

                                                disabled={createTemplateMutation.isLoading}
                                                className="px-10 py-3.5 text-white rounded-xl flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all font-bold text-lg relative overflow-hidden"
                                            >
                                                {createTemplateMutation.isLoading ? (
                                                    <>
                                                        {/* Animated gradient background */}
                                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 via-pink-600 to-indigo-600 animate-[gradient_3s_ease_infinite] bg-[length:200%_auto]"></div>
                                                        <Loader2 className="h-5 w-5 animate-spin relative z-10" />
                                                        <div className="relative z-10 flex items-center gap-2">
                                                            <span className="font-mono text-sm bg-black/20 px-3 py-1 rounded border border-white/30 backdrop-blur-sm">
                                                                {aiWritingStatus}...
                                                            </span>
                                                            <span className="text-xs animate-pulse">⚡</span>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>

                                                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 absolute inset-0"></div>
                                                        <Save className="h-5 w-5 relative z-10" />
                                                        <span className="relative z-10">Create Template</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    ) : (
                        // Edit mode - full width layout
                        <div className="bg-white rounded-2xl shadow-lg p-8">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <span className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                                        Template Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-5 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white font-medium"
                                        placeholder="Enter a name for your template"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                                            <span className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                                            Email Subject
                                        </label>
                                        <button
                                            onClick={() => setShowSubjectAiModal(true)}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-medium"
                                            title="Generate subject with AI"
                                        >
                                            <Sparkles className="h-4 w-4" />
                                            Modify Subject with AI
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        className="w-full px-5 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white font-medium"
                                        placeholder="Enter email subject line"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
                                            <span className="w-1 h-4 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                                            Email Content
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setShowAiModifyPrompt(!showAiModifyPrompt)}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-sm font-medium"
                                                title="Modify with AI"
                                            >
                                                <Sparkles className="h-4 w-4" />
                                                AI Modify
                                            </button>
                                            <button
                                                onClick={() => setShowEditPreview(!showEditPreview)}
                                                className="flex items-center gap-2 px-3 py-1.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium"
                                                title="Toggle Preview"
                                            >
                                                <Eye className="h-4 w-4" />
                                                {showEditPreview ? 'Edit' : 'Preview'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* AI Modification Prompt */}
                                    {showAiModifyPrompt && (
                                        <div className="mb-4 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200">
                                            <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                                <Sparkles className="w-4 h-4 text-purple-600" />
                                                AI Modification Instructions
                                            </label>
                                            <textarea
                                                value={aiModifyPrompt}
                                                onChange={(e) => setAiModifyPrompt(e.target.value)}
                                                className="w-full px-4 py-3 border-2 border-purple-200 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-medium resize-none mb-3"
                                                rows={3}
                                                placeholder="Describe how you want to modify the content... (e.g., 'Make it more professional and add a call-to-action button')"
                                            />
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => modifyWithAIMutation.mutate(aiModifyPrompt)}
                                                    disabled={modifyWithAIMutation.isLoading || !aiModifyPrompt}
                                                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    {modifyWithAIMutation.isLoading ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                            Modifying...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Sparkles className="h-4 w-4" />
                                                            Apply Changes
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowAiModifyPrompt(false);
                                                        setAiModifyPrompt('');
                                                    }}
                                                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm font-medium"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Content Display - Edit or Preview */}
                                    {showEditPreview && isHTMLContent(formData.content) ? (
                                        <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50 min-h-[400px]">
                                            <div
                                                className="prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{ __html: formData.content }}
                                            />
                                        </div>
                                    ) : (
                                        <textarea
                                            value={formData.content}
                                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-gray-50 hover:bg-white font-medium resize-none"
                                            rows={14}
                                            placeholder="Write your email content here..."
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-between items-center mt-10 pt-8 border-t-2 border-gray-100">
                                <button
                                    onClick={() => navigate('/email-templates')}
                                    className="px-8 py-3.5 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold text-gray-700 hover:text-gray-900 transform hover:scale-105"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={createTemplateMutation.isLoading}
                                    className="px-10 py-3.5 text-white rounded-xl flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all font-bold text-lg relative overflow-hidden"
                                >
                                    {createTemplateMutation.isLoading ? (
                                        <>
                                            {/* Animated gradient background */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 via-pink-600 to-indigo-600 animate-[gradient_3s_ease_infinite] bg-[length:200%_auto]"></div>
                                            <Loader2 className="h-5 w-5 animate-spin relative z-10" />
                                            <div className="relative z-10 flex items-center gap-2">
                                                <span className="font-mono text-sm bg-black/20 px-3 py-1 rounded border border-white/30 backdrop-blur-sm">
                                                    {aiWritingStatus}...
                                                </span>
                                                <span className="text-xs animate-pulse">⚡</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 absolute inset-0"></div>
                                            <Save className="h-5 w-5 relative z-10" />
                                            <span className="relative z-10">Update Template</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && generatedContent && (
                <ModalPortal>
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Eye className="w-6 h-6 text-white" />
                                <h2 className="text-xl font-bold text-white">Preview Generated Template</h2>
                            </div>
                            <button
                                onClick={() => setShowPreview(false)}
                                className="text-white hover:text-red-200 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">{generatedContent.name}</h3>
                                {generatedContent.subject && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                        <p className="text-sm font-medium text-blue-800 mb-1">📧 Generated Email Subject:</p>
                                        <p className="text-sm text-blue-700 font-semibold">{generatedContent.subject}</p>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="px-2 py-1 bg-gray-100 rounded-full">Type: {generatedContent.template_type}</span>
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">✨ AI Generated</span>
                                </div>
                            </div>

                            {/* Content Preview */}
                            <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
                                {generatedContent.template_type === 'html' || isHTMLContent(generatedContent.content) ? (
                                    <div
                                        className="prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{ __html: generatedContent.content }}
                                    />
                                ) : (
                                    <div className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
                                        {generatedContent.content}
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* Modal Footer */}
                        <div className="border-t-2 border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowPreview(false);
                                    queryClient.invalidateQueries(['emailTemplates', currentWorkspace?.id]);
                                    navigate('/email-templates');
                                }}
                                className="px-6 py-2.5 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold text-gray-700 hover:text-gray-900"
                            >
                                Go to Templates
                            </button>
                            <button
                                onClick={() => {
                                    setShowPreview(false);
                                    setGeneratedContent(null);
                                }}
                                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all font-bold"
                            >
                                Create Another
                            </button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}

            {/* Subject AI Generation Modal */}
            {showSubjectAiModal && (
                <ModalPortal>
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Sparkles className="w-6 h-6 text-white" />
                                <h2 className="text-xl font-bold text-white">Generate Email Subject with AI</h2>
                            </div>
                            <button
                                onClick={() => {
                                    setShowSubjectAiModal(false);
                                    setSubjectPrompt('');
                                    setGeneratedSubjects([]);
                                    setSelectedSubject('');
                                }}
                                className="text-white hover:text-red-200 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Current Subject Display */}
                            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                                <p className="text-sm font-medium text-gray-700 mb-2">📧 Current Subject:</p>
                                <p className="text-gray-900 font-semibold">{formData.subject || 'No subject set'}</p>
                            </div>

                            {/* Prompt Input */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-purple-600" />
                                    Describe what you want for the subject line
                                </label>
                                <textarea
                                    value={subjectPrompt}
                                    onChange={(e) => setSubjectPrompt(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-purple-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-medium resize-none"
                                    rows={3}
                                    placeholder="e.g., 'Make it more engaging and urgent for a promotional email' or 'Create a friendly welcome subject for new users'"
                                />
                                <div className="mt-3">
                                    <button
                                        onClick={generateSubjectOptions}
                                        disabled={subjectGenerating || !subjectPrompt.trim()}
                                        className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {subjectGenerating ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="h-4 w-4" />
                                                Generate Subject Options
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Generated Subject Options */}
                            {generatedSubjects.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        ✨ Generated Subject Options
                                        <span className="text-sm font-normal text-gray-500">(Select one)</span>
                                    </h3>
                                    <div className="space-y-3">
                                        {generatedSubjects.map((subject, index) => (
                                            <div
                                                key={index}
                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedSubject === subject
                                                    ? 'border-purple-500 bg-purple-50'
                                                    : 'border-gray-200 hover:border-purple-300 hover:bg-purple-25'
                                                    }`}
                                                onClick={() => setSelectedSubject(subject)}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 ${selectedSubject === subject
                                                        ? 'border-purple-500 bg-purple-500'
                                                        : 'border-gray-300'
                                                        }`}>
                                                        {selectedSubject === subject && (
                                                            <div className="w-2 h-2 bg-white rounded-full"></div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="font-semibold text-gray-900">{subject}</p>
                                                        <p className="text-xs text-gray-500 mt-1">Option {index + 1}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="border-t-2 border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowSubjectAiModal(false);
                                    setSubjectPrompt('');
                                    setGeneratedSubjects([]);
                                    setSelectedSubject('');
                                }}
                                className="px-6 py-2.5 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold text-gray-700 hover:text-gray-900"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={applySelectedSubject}
                                disabled={!selectedSubject}
                                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <Save className="h-4 w-4" />
                                Apply Selected Subject
                            </button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
        </>
    );
};

export default TemplateCreator;

// Inline helper component: previews personalization replacements
const TemplatePersonalizationPreview = ({ subject, content }) => {
    const [sampleName, setSampleName] = React.useState('Nikhil Sharma');
    const [sampleEmail, setSampleEmail] = React.useState('nikhil@example.com');

    const deriveFirstName = (fullName, email) => {
        if (fullName && typeof fullName === 'string') {
            const trimmed = fullName.trim();
            if (trimmed.length > 0) {
                return trimmed.split(/\s+/)[0];
            }
        }
        if (email && typeof email === 'string') {
            const local = email.split('@')[0] || '';
            return local.replace(/[._-]+/g, ' ').split(/\s+/)[0] || 'there';
        }
        return 'there';
    };

    const renderText = (text) => {
        const nameValue = sampleName || deriveFirstName(null, sampleEmail) || 'there';
        const firstNameValue = deriveFirstName(sampleName, sampleEmail);
        const emailValue = sampleEmail || '';
        if (!text) return '';
        return text
            .replace(/\{\{\s*name\s*\}\}/gi, nameValue)
            .replace(/\{name\}/gi, nameValue)
            .replace(/\{\{\s*first_name\s*\}\}/gi, firstNameValue)
            .replace(/\{first_name\}/gi, firstNameValue)
            .replace(/\{\{\s*email\s*\}\}/gi, emailValue)
            .replace(/\{email\}/gi, emailValue);
    };

    const renderedSubject = renderText(subject);
    const renderedContent = renderText(content);

    const isHtml = (html) => /<[a-z][\s\S]*>/i.test(html || '');

    return (
        <div className="mt-4 p-4 bg-gray-50 border-2 border-gray-200 rounded-xl">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
                <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Sample name</label>
                    <input
                        type="text"
                        value={sampleName}
                        onChange={(e) => setSampleName(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                        placeholder="e.g., Amit Kumar"
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Sample email</label>
                    <input
                        type="email"
                        value={sampleEmail}
                        onChange={(e) => setSampleEmail(e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                        placeholder="e.g., amit@example.com"
                    />
                </div>
            </div>
            <div className="mt-3">
                {renderedSubject && (
                    <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                        <div className="text-[11px] font-semibold text-blue-700 mb-1">Preview subject</div>
                        <div className="text-sm text-blue-900 font-medium">{renderedSubject}</div>
                    </div>
                )}
                <div className="border-2 border-gray-200 rounded-lg p-3 bg-white">
                    <div className="text-[11px] font-semibold text-gray-600 mb-2">Preview content</div>
                    {isHtml(renderedContent) ? (
                        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderedContent }} />
                    ) : (
                        <div className="whitespace-pre-wrap text-sm text-gray-800">{renderedContent || '—'}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

